import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { fabric } from 'fabric';

const FabricCanvas = forwardRef(function FabricCanvas({ width = 600, height = 200, onCanvasReady }, ref) {
  const canvasEl = useRef(null);
  const fabricRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getCanvas: () => fabricRef.current,
    getJSON: () => {
      if (!fabricRef.current) return {};
      return fabricRef.current.toJSON();
    },
    loadJSON: (json) => {
      if (!fabricRef.current || !json) return;
      fabricRef.current.loadFromJSON(json, () => {
        fabricRef.current.renderAll();
      });
    },
    addText: (text = 'Your Text', opts = {}) => {
      if (!fabricRef.current) return;
      const t = new fabric.IText(text, {
        left: 20,
        top: 20,
        fontSize: 18,
        fontFamily: 'Arial',
        fill: '#1a1a1a',
        ...opts,
      });
      fabricRef.current.add(t);
      fabricRef.current.setActiveObject(t);
      fabricRef.current.renderAll();
    },
    addRect: (opts = {}) => {
      if (!fabricRef.current) return;
      const r = new fabric.Rect({
        left: 50,
        top: 50,
        width: 120,
        height: 50,
        fill: '#dbeafe',
        rx: 4,
        ry: 4,
        ...opts,
      });
      fabricRef.current.add(r);
      fabricRef.current.renderAll();
    },
    addCircle: (opts = {}) => {
      if (!fabricRef.current) return;
      const c = new fabric.Circle({
        left: 50,
        top: 50,
        radius: 35,
        fill: '#ede9fe',
        ...opts,
      });
      fabricRef.current.add(c);
      fabricRef.current.renderAll();
    },
    addImage: (url) => {
      if (!fabricRef.current) return;
      fabric.Image.fromURL(
        url,
        (img) => {
          if (!img) return;
          const maxW = Math.min(160, fabricRef.current.width / 3);
          img.scaleToWidth(maxW);
          img.set({ left: 20, top: 20 });
          fabricRef.current.add(img);
          fabricRef.current.setActiveObject(img);
          fabricRef.current.renderAll();
        },
        { crossOrigin: 'anonymous' }
      );
    },
    deleteSelected: () => {
      if (!fabricRef.current) return;
      const active = fabricRef.current.getActiveObject();
      if (active) {
        fabricRef.current.remove(active);
        fabricRef.current.discardActiveObject();
        fabricRef.current.renderAll();
      }
    },
    toDataURL: (opts = {}) => {
      if (!fabricRef.current) return '';
      return fabricRef.current.toDataURL({ format: 'png', quality: 1, ...opts });
    },
    clear: () => {
      if (fabricRef.current) {
        fabricRef.current.clear();
        fabricRef.current.backgroundColor = '#ffffff';
        fabricRef.current.renderAll();
      }
    },
  }));

  useEffect(() => {
    const canvas = new fabric.Canvas(canvasEl.current, {
      width,
      height,
      backgroundColor: '#ffffff',
      selection: true,
      preserveObjectStacking: true,
    });
    fabricRef.current = canvas;

    if (onCanvasReady) {
      onCanvasReady(canvas);
    }

    return () => {
      canvas.dispose();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle size changes
  useEffect(() => {
    if (fabricRef.current) {
      fabricRef.current.setWidth(width);
      fabricRef.current.setHeight(height);
      fabricRef.current.renderAll();
    }
  }, [width, height]);

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden shadow-sm inline-block bg-white">
      <canvas ref={canvasEl} />
    </div>
  );
});

export default FabricCanvas;
